<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <li
        v-if="!isInRemoteSelected"
        v-show="forceVisible || visible"
        ref="optionRef"
        class="el-select-dropdown__item"
        :title="title"
        :class="{
            'selected': isSelected && !showCheckbox && showSelectedHighlighting,
            'is-disabled': disabled || groupDisabled || limitReached,
            'hover': hover && select.keyboardNavigate.value,
            'is-multi-line': isMultiLine
        }"
        @click="selectOptionClick"
        @touchend.prevent="selectOptionTouchEnd"
    >
        <slot>
            <template v-if="!!$slots.prefix">
                <slot name="prefix" />
            </template>
            <el-checkbox
                v-if="showCheckbox"
                v-model="isSelected"
                :indeterminate="indeterminate"
                :disabled="disabled"
                @click.prevent
            />
            <a-glyph
                v-if="icon"
                :name="icon"
                :fill="iconColor"
                class="el-select-dropdown__item-icon"
            />
            <span
                v-if="!isMultiLine"
                :class="!!$slots.suffix ? 'el-select-dropdown__suffix' : null"
            >
                <span :key="highlightPayload.keyword" v-highlight="highlightPayload">{{ text }}</span>
            </span>
            <span
                v-else
                :class="!!$slots.suffix ? 'el-select-dropdown__suffix' : null"
            >
                <span :key="highlightPayload.keyword" v-highlight="highlightPayload" class="label">{{ text }}</span>
                <span>
                    <a-glyph
                        v-for="(rightIcon, i) in rightIcons"
                        :key="i"
                        :name="rightIcon.name"
                        :fill="rightIcon.color"
                        class="el-select-dropdown__item-icon"
                    />
                </span>
                <span
                    :key="subLabelHighlightPayload.keyword"
                    v-highlight="subLabelHighlightPayload"
                    class="sublabel"
                >
                    {{ subLabel }}
                </span>
            </span>
            <template v-if="!!$slots.suffix">
                <slot name="suffix" />
            </template>
        </slot>
    </li>
</template>

<script setup>
import { isEqual } from 'lodash-es';
import { ref, computed, inject, provide, onMounted, onBeforeUnmount, watch } from 'vue';
import ElCheckbox from '@core/components/checkbox/checkbox.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import Highlight from '@utils/v-highlight/v-highlight.js';

const select = inject('select');
const optionGroup = inject('optionGroup', null);

const props = defineProps({
    value: { type: [String, Number], required: true },
    label: { type: [String, Number], default: '' },
    subLabel: { type: [String, Number], default: '' },
    created: Boolean,
    disabled: { type: Boolean, default: false },
    icon: { type: String, default: '' },
    iconColor: { type: String, default: 'brand-primary' },
    rightIcons: { type: Array, default: () => [] },
    forceVisible: { type: Boolean, default: false },
});

defineOptions({
    name: 'ElOption',
    directives: {
        highlight: Highlight,
    },
});
const optionRef = ref(null);
const groupDisabled = ref(false);
const index = ref(-1);
const hitState = ref(false);
const hover = ref(false);
const title = ref('');

const showCheckbox = computed(() => select.props.multiple);
const isObject = computed(() => Object.prototype.toString.call(props.value).toLowerCase() === '[object object]');
const text = computed(() => currentLabel.value);
const highlightPayload = computed(() => ({
    text: text.value,
    keyword: select.query.value || '',
}));
const subLabelHighlightPayload = computed(() => ({
    text: props.subLabel,
    keyword: select.query.value ?? '',
}));
const currentLabel = computed(() => props.label || (!isObject.value ? props.value : ''));
const indeterminate = computed(() =>
    select.props.multiple &&
    select.selectAll?.value &&
    props.value === 'all' &&
    select.selected?.value?.length > 0 &&
    select.options?.value?.[0]?.isSelected !== true,
);
const isSelected = computed(() => {
    const value = props.value;
    const modelValue = select.props.modelValue;
    if (select.props.multiple) {
        return Array.isArray(modelValue) && modelValue.some(v => isEqual(v, value));
    }
    return isEqual(value, modelValue);
});

const showSelectedHighlighting = computed(() => !(isSelected.value && select.keyboardNavigate.value && hover.value));
const limitReached = computed(() =>
    select.props.multiple &&
    !isSelected.value &&
    (select.props.modelValue || []).length >= select.props.multipleLimit &&
    select.props.multipleLimit > 0,
);

const isInRemoteSelected = computed(() => {
    return !props.forceVisible &&
            select.props.remote && select.props.filterable &&
            select.props.multiple && select.previousSelected.value?.length &&
            !select.query.value.length &&
            select.previousSelected.value?.some(opt => isEqual(opt.value, props.value));
});

const isMultiLine = computed(() => props.subLabel !== '');
const isEnabled = computed(() => !props.disabled && !groupDisabled.value);
const visible = computed(() => {
    if (typeof select?.props.filterMethod === 'function') return true;
    if (typeof select?.props.remoteMethod === 'function') return true;
    if (!select?.props.filterable || !select?.query) return true;

    const queryText = select.query.value.toLowerCase();
    const labelText = props.label?.toString().toLowerCase();

    return labelText.includes(queryText);
});


function selectOptionClick() {
    if (!('ontouchend' in document.documentElement) && isEnabled.value) {
        select.handleOptionClick?.(optionProxy);
    }
}

function selectOptionTouchEnd() {
    if (isEnabled.value) {
        select.handleOptionClick?.(optionProxy);
    }
}

function handleGroupDisabled(val) {
    groupDisabled.value = val;
}

function initTitleAttr() {
    title.value = props.label;
}

const optionProxy = {
    value: props.value,
    label: props.label,
    isSelected,
    icon: props.icon,
    iconColor: props.iconColor,
    currentLabel,
    visible,
    optionRef,
    hover,
};

onMounted(() => {
    initTitleAttr();
    select.registerOption?.(optionProxy);
    optionGroup?.registerGroupOption?.(optionProxy);
});


onBeforeUnmount(() => {
    select.unregisterOption?.(optionProxy);
    optionGroup?.unregisterGroupOption?.(optionProxy);
});

watch(() => props.value, () => {
    if (!props.created && !select.props.remote) {
        select.setSelected?.();
    }
});

watch(currentLabel, (value) => {
    if (!props.created && !select.props.remote) {
        select.setSelected?.();
    }
    text.value = value;
});

provide('option', {
    props,
    index,
    title,
    visible,
    hitState,
    handleGroupDisabled,
});

defineExpose({
    optionRef,
    visible,
});
</script>


<style scoped lang="postcss">
.el-select-dropdown__item {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    padding: 0 16px;
    position: relative;
    height: 40px;
    line-height: 2.86;
    box-sizing: border-box;
    text-align: left;
    cursor: pointer;
    &.selected {
        color: var(--av-fixed-primary);
        font-weight: 400;
        background-color: var(--el-secondary-active);
    }
    &:hover, &.hover {
        background-color: var(--el-secondary-hover);
    }
    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        line-height: 32px !important;
    }
    :deep(.el-text--highlighting) {
        background: var(--av-fixed-highlight);
    }
}
</style>
